import {
  Modal,
  ModalContent,
  type ModalHook,
  ProfileImage,
  useControlledModalState,
} from '@leland-dev/leland-ui-library';
import { type DocumentNode } from 'graphql';
import React, { type FC } from 'react';

import {
  type EntityReviewInput,
  type ReviewInput,
} from '../../../__generated-gql-types__/globalTypes';
import { isSessionBootcamp } from '../../../utils/session';
import { type PostSessionReviewType } from '../../../utils/types';
import { BootcampReviewForm } from '../BootcampReviewForm';
import { BootcampSessionReviewForm } from '../BootcampSessionReviewForm';
import { ReviewForm } from '../ReviewForm';

import type {
  LeaveAReviewModalCoachFragment,
  LeaveAReviewModalSessionFragment,
} from './__generated-gql-types__/LeaveAReviewModal.generated';

interface LeaveAReviewModalHookProps {
  coach: LeaveAReviewModalCoachFragment;
  session?: LeaveAReviewModalSessionFragment;
  defaultValues?: ReviewInput | EntityReviewInput;
  defaultBootcampSessionValues?: EntityReviewInput;
  reviewType?: PostSessionReviewType;
  onSuccess?: () => void;
  refetchQueries?: DocumentNode[];
}

interface LeaveAReviewModalProps extends LeaveAReviewModalHookProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const LeaveAReviewModalContent: FC<LeaveAReviewModalProps> = ({
  open,
  setOpen,
  coach,
  session,
  defaultValues,
  defaultBootcampSessionValues,
  reviewType = 'coach',
  onSuccess,
  refetchQueries,
}) => {
  const Header =
    reviewType === 'bootcamp' ? (
      'Review this bootcamp'
    ) : reviewType === 'bootcamp_session' ? (
      'Review this session'
    ) : (
      <>
        <ProfileImage className="size-6" {...coach.user} />
        <p className="ml-2.5">Leave {coach.user.firstName} a Review</p>
      </>
    );

  return (
    <Modal open={open} onOpenChange={setOpen}>
      <ModalContent header={Header} preventScroll>
        {reviewType === 'coach' ? (
          <ReviewForm
            coach={coach}
            defaultValues={defaultValues}
            onSuccess={onSuccess}
            onRequestClose={() => setOpen(false)}
          />
        ) : null}
        {reviewType === 'bootcamp' && isSessionBootcamp(session?.content) ? (
          <BootcampReviewForm
            bootcampCohortUrn={session.content.cohort.id}
            defaultValues={defaultValues as EntityReviewInput}
            onSuccess={onSuccess}
            onRequestClose={() => setOpen(false)}
          />
        ) : null}
        {reviewType === 'bootcamp_session' &&
        isSessionBootcamp(session?.content) ? (
          <BootcampSessionReviewForm
            bootcampSessionUrn={session.id}
            categoryUrn={session.content.bootcamp.category.id}
            defaultValues={defaultBootcampSessionValues}
            onSuccess={onSuccess}
            onRequestClose={() => setOpen(false)}
            refetchQueries={refetchQueries}
          />
        ) : null}
      </ModalContent>
    </Modal>
  );
};

export const useLeaveAReviewModal: ModalHook<
  LeaveAReviewModalHookProps
> = () => {
  const [open, setOpen] = useControlledModalState();
  const LeaveAReviewModal: FC<LeaveAReviewModalProps> = (props) => {
    return open ? (
      <LeaveAReviewModalContent {...props} open={open} setOpen={setOpen} />
    ) : null;
  };
  return { open, setOpen, Modal: LeaveAReviewModal };
};
